// Scenery around the course: pine forest and birch groves, the lake with islands and a far
// shore, pennant flags on the grandstand roof and lake-side poles, and drifting pollen motes in
// the summer sun. The season shows on it: birch leaves green, yellow or gone, snow on the
// spruces, ice on the lake. Everything is instanced or merged so the backdrop stays cheap.
import {
  AdditiveBlending, BufferAttribute, BufferGeometry, Color, CylinderGeometry,
  DoubleSide, Group, IcosahedronGeometry, InstancedMesh, Matrix4, Mesh, MeshStandardMaterial,
  PlaneGeometry, Points, Quaternion, ShaderMaterial, Vector3, type Camera, type CanvasTexture,
} from 'three';
import { TRACK_LENGTH } from '../../data/track';
import { COURSE, HOARDING, LAKE, PODIUM, STAND, rng, standModulesX, treeSpots } from './layout';
import type { SeasonState } from './calendar';
import { GLSL_FLOOD_REACH, SEASON_UNIFORMS } from './seasonUniforms';
import { GLSL_NOISE, canvasTexture, merge, normalise, place, shade } from './util';

/** The stadium's own pennant colours. */
const FLAG_COLOURS = ['#ff3fa4', '#ffd23f', '#8a4dff', '#b0405a', '#f6efe2'];

const WIND_GLSL = /* glsl */ `
uniform float uTime;
`;

/** Colours a geometry's vertices with a function of their position. */
function paintVertices(g: BufferGeometry, f: (p: Vector3) => [number, number, number]): BufferGeometry {
  const n = normalise(g);
  const pos = n.getAttribute('position');
  const col = n.getAttribute('color') as BufferAttribute;
  const v = new Vector3();
  for (let i = 0; i < pos.count; i++) { v.fromBufferAttribute(pos, i); const [r, gg, b] = f(v); col.setXYZ(i, r, gg, b); }
  return n;
}

/** Wind sway for trunks: displacement grows with height. */
function swayMaterial(name: string, uniforms: { uTime: { value: number } }): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ vertexColors: true, roughness: 0.9, name });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = uniforms.uTime;
    shader.uniforms.uFlood = SEASON_UNIFORMS.uFlood;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${WIND_GLSL}\nvarying vec3 vTreeBase;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec4 wbase = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        vTreeBase = (modelMatrix * wbase).xyz;
        float sway = max(0.0, position.y - 2.0) * 0.018;
        transformed.x += sin(uTime * 0.9 + wbase.x * 0.13 + wbase.z * 0.07) * sway;
        transformed.z += cos(uTime * 0.7 + wbase.x * 0.11) * sway * 0.6;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying vec3 vTreeBase;\n${GLSL_FLOOD_REACH}`)
      .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.rgb *= floodReach(vTreeBase);');
  };
  m.customProgramCacheKey = () => `sway-${name}`;
  return m;
}

/** A leaf-cluster card texture: many small leaves (birch) or needle sprays (pine) on twigs. */
function foliageTexture(kind: 'birch' | 'pine'): CanvasTexture {
  const r = rng(kind === 'birch' ? 401 : 402);
  const t = canvasTexture(512, 512, (g, w, h) => {
    const cx = w / 2, cy = h / 2;
    if (kind === 'birch') {
      g.strokeStyle = '#4a3a28'; g.lineWidth = 3;
      for (let i = 0; i < 7; i++) {
        const a = r() * Math.PI * 2;
        g.beginPath(); g.moveTo(cx, cy); g.quadraticCurveTo(cx + Math.cos(a) * 90, cy + Math.sin(a) * 90 + 30, cx + Math.cos(a) * 210, cy + Math.sin(a) * 210); g.stroke();
      }
      for (let i = 0; i < 260; i++) {
        const a = r() * Math.PI * 2, d = Math.pow(r(), 0.6) * 225;
        const x = cx + Math.cos(a) * d, y = cy + Math.sin(a) * d;
        const l = 15 + r() * 13;
        const hue = 70 + r() * 30, light = 26 + r() * 22, sat = 45 + r() * 25;
        g.save(); g.translate(x, y); g.rotate(r() * Math.PI * 2);
        g.fillStyle = `hsl(${hue},${sat}%,${light}%)`;
        g.beginPath(); g.moveTo(0, -l); g.quadraticCurveTo(l * 0.62, -l * 0.2, 0, l * 0.55); g.quadraticCurveTo(-l * 0.62, -l * 0.2, 0, -l); g.fill();
        g.strokeStyle = `hsla(${hue},40%,${light + 14}%,0.5)`; g.lineWidth = 1;
        g.beginPath(); g.moveTo(0, -l * 0.9); g.lineTo(0, l * 0.5); g.stroke();
        g.restore();
      }
    } else {
      // A drooping spruce branch: a main twig with dense needle sprays along it.
      for (let b = 0; b < 3; b++) {
        const y0 = cy - 120 + b * 120 + (r() - 0.5) * 30;
        g.strokeStyle = '#3a2a1c'; g.lineWidth = 4;
        g.beginPath(); g.moveTo(8, y0); g.quadraticCurveTo(w * 0.5, y0 - 30, w - 10, y0 + 40); g.stroke();
        for (let i = 0; i < 1400; i++) {
          const t2 = r();
          const x = 8 + t2 * (w - 18);
          const y = y0 - 30 * Math.sin(t2 * Math.PI) + t2 * 40 + (r() - 0.5) * 80 * Math.sin(t2 * Math.PI * 0.9 + 0.2);
          const a = (r() - 0.5) * 2.2 + Math.PI / 2;
          const l = 10 + r() * 14;
          const light = 14 + r() * 18;
          g.strokeStyle = `hsl(${120 + r() * 30},${35 + r() * 25}%,${light}%)`;
          g.lineWidth = 2.4;
          g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * l * (r() < 0.5 ? 1 : -1), y + Math.sin(a) * l * 0.6); g.stroke();
        }
      }
    }
  });
  t.anisotropy = 4;
  return t;
}

/** A full spruce silhouette for crossed cards: drooping tiers of needles, lit on their tops. */
function spruceTexture(): CanvasTexture {
  const r = rng(403);
  const t = canvasTexture(512, 1024, (g, w, h) => {
    const cx = w / 2;
    g.fillStyle = '#2e2016'; g.fillRect(cx - 7, h * 0.55, 14, h * 0.45);
    const tiers = 22;
    for (let i = 0; i < tiers; i++) {
      const tt = i / (tiers - 1);
      const y = 30 + tt * (h * 0.86);
      const half = 18 + tt * (w * 0.46 - 18);
      for (const dir of [-1, 1]) {
        // A drooping branch from the trunk to its tip, thick with needles.
        for (let k = 0; k < 150 + tt * 220; k++) {
          const u = Math.pow(r(), 0.8);
          const bx = cx + dir * u * half;
          const by = y + u * u * 34 + (r() - 0.5) * (14 + 26 * (1 - u));
          const a = Math.PI / 2 + dir * (0.4 + r() * 0.9) + (r() - 0.5) * 0.6;
          const l = 7 + r() * 11;
          const top = by < y + u * u * 34;
          const light = (top ? 18 : 9) + r() * 10 + (1 - u) * -3;
          g.strokeStyle = `hsl(${128 + r() * 24},${32 + r() * 22}%,${light}%)`;
          g.lineWidth = 1.8;
          g.beginPath(); g.moveTo(bx, by); g.lineTo(bx + Math.cos(a) * l * 0.7, by + Math.sin(a) * l); g.stroke();
        }
      }
    }
  });
  t.anisotropy = 4;
  return t;
}

/** Three crossed vertical cards carrying the spruce silhouette, with normals bent outwards. */
function spruceCards(): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const H = 14.5, W = 6.4;
  for (let i = 0; i < 3; i++) {
    const g = normalise(place(new PlaneGeometry(W, H, 4, 8), [0, 0.6 + H / 2, 0], [0, (i * Math.PI) / 3, 0]));
    const pos = g.getAttribute('position'), nor = g.getAttribute('normal') as BufferAttribute;
    const v = new Vector3();
    for (let k = 0; k < pos.count; k++) {
      v.fromBufferAttribute(pos, k);
      const n = new Vector3(v.x, 0, v.z);
      if (n.lengthSq() < 1e-4) n.set(0, 1, 0); else n.normalize().multiplyScalar(0.85).add(new Vector3(0, 0.5, 0)).normalize();
      nor.setXYZ(k, n.x, n.y, n.z);
    }
    parts.push(g);
  }
  return merge(parts);
}

/** Quads with normals bent away from a centre, so a crown of cards shades like a soft volume. */
function cardCrown(cards: { at: Vector3; size: number; rot: [number, number, number] }[], centre: (p: Vector3) => Vector3, tint: () => [number, number, number]): BufferGeometry {
  const parts: BufferGeometry[] = [];
  for (const c of cards) {
    const g = normalise(place(new PlaneGeometry(c.size, c.size), [c.at.x, c.at.y, c.at.z], c.rot));
    const pos = g.getAttribute('position'), nor = g.getAttribute('normal') as BufferAttribute, col = g.getAttribute('color') as BufferAttribute;
    const v = new Vector3();
    const [tr, tg, tb] = tint();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const n = v.clone().sub(centre(v)).normalize();
      nor.setXYZ(i, n.x, n.y, n.z);
      const lift = 0.8 + 0.25 * Math.max(-1, Math.min(1, n.y));
      col.setXYZ(i, tr * lift, tg * lift, tb * lift);
    }
    parts.push(g);
  }
  return merge(parts);
}

function birchParts(): { trunk: BufferGeometry; leaves: BufferGeometry } {
  const r = rng(5);
  const trunk = paintVertices(place(new CylinderGeometry(0.09, 0.17, 10, 6, 14), [0, 5, 0]), (p) => {
    // White bark with dark horizontal marks and a dark base.
    const mark = Math.sin(p.y * 9.0 + Math.sin(p.x * 30) * 2) > 0.8 || Math.sin(p.y * 3.7 + p.z * 20) > 0.93;
    const base = Math.max(0, 1 - p.y / 1.4);
    const w = mark ? 0.1 : 0.82;
    const c = w * (1 - base * 0.75);
    return [c, c * 0.98, c * 0.94];
  });
  const crownC = new Vector3(0, 8.2, 0);
  const cards: { at: Vector3; size: number; rot: [number, number, number] }[] = [];
  for (let i = 0; i < 46; i++) {
    const a = r() * Math.PI * 2, u = r() * 2 - 1, d = Math.pow(r(), 0.5);
    const at = new Vector3(Math.cos(a) * Math.sqrt(1 - u * u) * 2.3 * d, crownC.y + u * 3.4 * d, Math.sin(a) * Math.sqrt(1 - u * u) * 2.3 * d);
    cards.push({ at, size: 1.5 + r() * 0.9, rot: [r() * Math.PI, r() * Math.PI, r() * Math.PI] });
  }
  const leaves = cardCrown(cards, () => crownC, () => { const k = 0.9 + r() * 0.2; return [k, k, k * 0.95]; });
  return { trunk, leaves };
}

function pineParts(): { trunk: BufferGeometry; leaves: BufferGeometry } {
  const r = rng(6);
  const trunk = paintVertices(place(new CylinderGeometry(0.08, 0.26, 13, 6), [0, 6.5, 0]), () => [0.22, 0.15, 0.1]);
  const cards: { at: Vector3; size: number; rot: [number, number, number] }[] = [];
  const tiers = 7;
  for (let t = 0; t < tiers; t++) {
    const y = 2.2 + t * 1.6;
    const reach = 2.7 * (1 - t / tiers) + 0.4;
    const n = 5;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + r() * 0.8 + t * 0.9;
      const at = new Vector3(Math.cos(a) * reach * 0.55, y, Math.sin(a) * reach * 0.55);
      // Branch card lies along its direction, drooping outwards.
      cards.push({ at, size: reach * 1.2 + 0.5, rot: [-Math.PI / 2 + 0.45, -a, 0] });
    }
  }
  const leaves = cardCrown(cards, (p) => new Vector3(0, p.y - 0.8, 0), () => { const k = 0.85 + r() * 0.3; return [k, k, k]; });
  return { trunk, leaves };
}

/**
 * Foliage material: alpha-tested leaf cards, wind sway, and sunlight glowing through the leaves.
 * Birches follow the season (fresh spring green, autumn yellow, bare twigs in winter); spruces
 * and pines carry snow on their upper sides.
 */
function foliageMaterial(kind: 'birch' | 'spruce' | 'pine', map: CanvasTexture, wind: { uTime: { value: number } }, sunDir: Vector3): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ map, alphaTest: 0.45, side: DoubleSide, vertexColors: true, roughness: 0.85, name: `${kind}-leaves` });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = wind.uTime;
    shader.uniforms.uSunDir = { value: sunDir };
    for (const k of ['uLeaves', 'uAutumn', 'uSpring', 'uSnow', 'uSunGlow', 'uFlood'] as const) shader.uniforms[k] = SEASON_UNIFORMS[k];
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${WIND_GLSL}\nvarying vec3 vLeafWorld;\nvarying vec3 vTreeBase;\nvarying float vUpN;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec4 wbase = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        vTreeBase = (modelMatrix * wbase).xyz;
        vUpN = objectNormal.y;
        float sway = max(0.0, position.y - 2.0) * 0.018;
        float flutter = sin(uTime * 4.0 + position.x * 3.0 + position.z * 2.0 + wbase.x) * 0.04;
        transformed.x += sin(uTime * 0.9 + wbase.x * 0.13 + wbase.z * 0.07) * sway + flutter;
        transformed.z += cos(uTime * 0.7 + wbase.x * 0.11) * sway * 0.6;`)
      .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
        vLeafWorld = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform vec3 uSunDir;
uniform float uLeaves, uAutumn, uSpring, uSnow, uSunGlow;
varying vec3 vLeafWorld;
varying vec3 vTreeBase;
varying float vUpN;
${GLSL_FLOOD_REACH}
float leafHash(vec3 p) { return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        float cellHash = leafHash(vec3(floor(vMapUv * 14.0), floor(vTreeBase.x * 0.7) + floor(vTreeBase.z * 0.7) * 31.0));
        ${kind === 'birch' ? `
        // Leaves are green, twigs brown: only leaves turn and fall.
        float isLeaf = step(diffuseColor.r * 1.05, diffuseColor.g);
        if (isLeaf > 0.5 && cellHash > uLeaves) discard;
        float lum = dot(diffuseColor.rgb, vec3(0.3, 0.59, 0.11));
        vec3 autumnCol = mix(vec3(1.0, 0.66, 0.08), vec3(0.9, 0.34, 0.05), leafHash(vTreeBase + 3.0) * 0.8) * lum * 2.4;
        diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.15, 1.32, 0.7), uSpring * isLeaf);
        diffuseColor.rgb = mix(diffuseColor.rgb, autumnCol, uAutumn * isLeaf * (0.75 + 0.25 * cellHash));
        ` : `
        // Snow resting on the upper sides of the branches.
        float snowy = uSnow * smoothstep(0.15, 0.6, vUpN) * (0.55 + 0.45 * cellHash);
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.76, 0.8, 0.86), snowy * 0.75);
        `}
        diffuseColor.rgb *= floodReach(vTreeBase);`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        vec3 toCam = normalize(cameraPosition - vLeafWorld);
        float through = pow(max(dot(-toCam, uSunDir), 0.0), 6.0);
        totalEmissiveRadiance += diffuseColor.rgb * vec3(1.6, 1.25, 0.55) * (through * 1.4 + 0.06) * uSunGlow;`);
  };
  m.customProgramCacheKey = () => `foliage-${kind}`;
  return m;
}

function rippleNormal(): CanvasTexture {
  const r = rng(31);
  return canvasTexture(256, 256, (g, w, h) => {
    const img = g.createImageData(w, h);
    const waves = Array.from({ length: 14 }, () => ({ kx: (r() - 0.5) * 0.5, ky: (r() - 0.5) * 0.18, p: r() * 6.28, a: 0.3 + r() * 0.7 }));
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let dx = 0, dy = 0;
      for (const wv of waves) {
        // Integer cycles across the tile keep it seamless.
        const fx = Math.round(wv.kx * w / 6.28) * 6.28 / w, fy = Math.round(wv.ky * h / 6.28) * 6.28 / h;
        const c = Math.cos(x * fx + y * fy + wv.p) * wv.a;
        dx += c * fx * 6; dy += c * fy * 6;
      }
      const len = Math.hypot(dx, dy, 1);
      const i = (y * w + x) * 4;
      img.data[i] = (-dx / len * 0.5 + 0.5) * 255; img.data[i + 1] = (-dy / len * 0.5 + 0.5) * 255;
      img.data[i + 2] = (1 / len * 0.5 + 0.5) * 255; img.data[i + 3] = 255;
    }
    g.putImageData(img, 0, 0);
  }, { data: true, repeat: true });
}

export class Props {
  readonly group = new Group();
  private wind = { uTime: { value: 0 } };
  private water: MeshStandardMaterial;
  private flagMat: MeshStandardMaterial;
  private motes: Points;
  private moteUniforms = { uTime: { value: 0 }, uCenter: { value: new Vector3() }, uAmount: { value: 1 } };
  /** Materials of the far scenery, with their summer colour, for snow and night. */
  private landMats: { mat: MeshStandardMaterial; base: Color; snow: Color }[] = [];
  private flags!: InstancedMesh;

  constructor(sunDir: Vector3) {
    this.group.name = 'scenery';
    const m = new Matrix4(), q = new Quaternion(), s = new Vector3(), p = new Vector3(), up = new Vector3(0, 1, 0);

    // Trees.
    const spots = treeSpots();
    const r = rng(17);
    // Island trees in the lake.
    const islands = [[TRACK_LENGTH * 0.62, 190, 26], [TRACK_LENGTH * 0.95, 260, 40], [TRACK_LENGTH * 0.25, 320, 34]] as const;
    for (const [ix, iz, rad] of islands) for (let k = 0; k < 40; k++) {
      const a = r() * Math.PI * 2, d = Math.sqrt(r()) * rad * 0.8;
      spots.push({ x: ix + Math.cos(a) * d, z: iz + Math.sin(a) * d * 0.6, scale: 0.9 + r() * 0.6, kind: 'pine', rot: r() * 6.28 });
    }
    const pines = spots.filter((t) => t.kind === 'pine');
    const birches = spots.filter((t) => t.kind === 'birch');
    const pine = pineParts(), birch = birchParts();
    const kinds = [
      { list: pines, trunk: pine.trunk, cards: [
        { geo: spruceCards(), mat: foliageMaterial('spruce', spruceTexture(), this.wind, sunDir) },
        { geo: pine.leaves, mat: foliageMaterial('pine', foliageTexture('pine'), this.wind, sunDir) },
      ], name: 'pines' },
      { list: birches, trunk: birch.trunk, cards: [
        { geo: birch.leaves, mat: foliageMaterial('birch', foliageTexture('birch'), this.wind, sunDir) },
      ], name: 'birches' },
    ];
    for (const k of kinds) {
      const meshes = [new InstancedMesh(k.trunk, swayMaterial(`${k.name}-trunk`, this.wind), k.list.length),
        ...k.cards.map((c) => new InstancedMesh(c.geo, c.mat, k.list.length))];
      k.list.forEach((t, i) => {
        const y = islands.some(([ix, iz, rad]) => Math.hypot(t.x - ix, (t.z - iz) / 0.6) < rad) ? LAKE.y + 1.2 : 0;
        m.compose(p.set(t.x, y, t.z), q.setFromAxisAngle(up, t.rot), s.set(t.scale, t.scale * (0.9 + (i % 7) * 0.04), t.scale));
        for (const im of meshes) im.setMatrixAt(i, m);
      });
      for (const im of meshes) { im.receiveShadow = true; im.castShadow = false; im.name = k.name; this.group.add(im); }
    }

    // Islands: low wooded mounds.
    const moundMat = new MeshStandardMaterial({ color: '#2c3a1c', roughness: 1 });
    this.landMats.push({ mat: moundMat, base: moundMat.color.clone(), snow: new Color('#b9c1cb') });
    for (const [ix, iz, rad] of islands) {
      const g = place(new IcosahedronGeometry(1, 3), [ix, LAKE.y - 1, iz], [0, 0, 0], [rad, 3.2, rad * 0.6]);
      this.group.add(new Mesh(g, moundMat));
    }

    // Lake shore bank and the water.
    const bankMat = new MeshStandardMaterial({ color: '#6b5a3c', roughness: 1 });
    this.landMats.push({ mat: bankMat, base: bankMat.color.clone(), snow: new Color('#c3cad3') });
    const bank = new Mesh(shade(place(new PlaneGeometry(2400, 12, 1, 1), [TRACK_LENGTH / 2, -0.35, LAKE.shoreZ + 9], [-Math.PI / 2 + 0.1, 0, 0]), [1, 1, 1]), bankMat);
    bank.receiveShadow = true;
    this.group.add(bank);
    const ripples = rippleNormal();
    ripples.repeat.set(97, 61);
    this.water = new MeshStandardMaterial({ color: '#1b2c44', roughness: 0.16, metalness: 0.0, normalMap: ripples, envMapIntensity: 1.1 });
    this.water.normalScale.set(0.22, 0.22);
    const water = new Mesh(new PlaneGeometry(4200, 2600).rotateX(-Math.PI / 2), this.water);
    water.position.set(TRACK_LENGTH / 2, LAKE.y, LAKE.shoreZ + 1300);
    water.receiveShadow = true;
    water.name = 'lake';
    this.group.add(water);

    // Far shores and hills: a ring of forested ridges fading into the haze.
    this.group.add(this.ridges());

    // Pennants on the grandstand roof and on poles along the lake side.
    this.flagMat = new MeshStandardMaterial({ roughness: 0.75, side: DoubleSide });
    this.flagMat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.wind.uTime;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${WIND_GLSL}`)
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          float fx = clamp(position.x / 1.6, 0.0, 1.0);
          vec4 fb = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          transformed.z += sin(uTime * 5.0 - position.x * 3.2 + fb.x * 0.3) * 0.16 * fx;
          transformed.y += sin(uTime * 3.1 - position.x * 2.0 + fb.x) * 0.04 * fx;`);
    };
    this.flagMat.customProgramCacheKey = () => 'pennant';
    const flagGeo = new BufferGeometry();
    const fv: number[] = [], fn: number[] = [], fuv: number[] = [];
    const segs = 8;
    for (let i = 0; i < segs; i++) {
      const x0 = (i / segs) * 1.6, x1 = ((i + 1) / segs) * 1.6;
      const h0 = 0.42 * (1 - i / segs), h1 = 0.42 * (1 - (i + 1) / segs);
      fv.push(x0, -h0, 0, x1, -h1, 0, x1, h1, 0, x0, -h0, 0, x1, h1, 0, x0, h0, 0);
      for (let k = 0; k < 6; k++) { fn.push(0, 0, 1); fuv.push(0, 0); }
    }
    flagGeo.setAttribute('position', new BufferAttribute(new Float32Array(fv), 3));
    flagGeo.setAttribute('normal', new BufferAttribute(new Float32Array(fn), 3));
    flagGeo.setAttribute('uv', new BufferAttribute(new Float32Array(fuv), 2));
    const poleSpots: [number, number, number][] = [];
    for (const x0 of standModulesX()) for (const off of [6, 18]) poleSpots.push([x0 + off, STAND.roofFrontY + 0.3, STAND.frontZ + 1.3]);
    for (let x = COURSE.from + 10; x < COURSE.to; x += 30) if (Math.abs(x - PODIUM.x) > 6) poleSpots.push([x, 0, HOARDING.z + 1.6]);
    const flagColours = FLAG_COLOURS.map((c) => new Color(c));
    const flags = new InstancedMesh(flagGeo, this.flagMat, poleSpots.length);
    const poleH = (y: number) => (y > 1 ? 2.6 : 6.5);
    const poles = new InstancedMesh(place(new CylinderGeometry(0.035, 0.05, 1, 6), [0, 0.5, 0]), new MeshStandardMaterial({ color: '#ece4d4', roughness: 0.5, metalness: 0.2 }), poleSpots.length);
    poleSpots.forEach(([x, y, z], i) => {
      const h = poleH(y);
      poles.setMatrixAt(i, m.compose(p.set(x, y, z), q.identity(), s.set(1, h, 1)));
      flags.setMatrixAt(i, m.compose(p.set(x, y + h - 0.3, z), q.setFromAxisAngle(up, -0.35 + (i % 3) * 0.1), s.set(1, 1, 1)));
      flags.setColorAt(i, flagColours[i % flagColours.length]!);
    });
    this.flags = flags;
    // No shadows: from the roof a low sun would stripe the whole course with them.
    for (const im of [flags, poles]) { im.receiveShadow = true; im.frustumCulled = false; this.group.add(im); }

    // Pollen and dust motes catching the low sun.
    this.motes = this.makeMotes();
    this.group.add(this.motes);
  }

  private ridges(): Mesh {
    const segs = 220, cx = TRACK_LENGTH / 2, cz = 200;
    const pos: number[] = [], col: number[] = [], idx: number[] = [];
    const layers = [{ r: 1250, h: 46, c: [0.13, 0.17, 0.2] }, { r: 1700, h: 80, c: [0.2, 0.2, 0.3] }];
    let base = 0;
    for (const L of layers) {
      for (let i = 0; i <= segs; i++) {
        const a = (i / segs) * Math.PI * 2;
        const n = 0.55 + 0.25 * Math.sin(a * 5 + L.r) + 0.15 * Math.sin(a * 13 + 1) + 0.08 * Math.sin(a * 31);
        const x = cx + Math.cos(a) * L.r, z = cz + Math.sin(a) * L.r;
        pos.push(x, -8, z, x, L.h * n, z);
        col.push(...L.c, ...L.c.map((c) => c * 1.15));
      }
      for (let i = 0; i < segs; i++) { const a = base + i * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
      base += (segs + 1) * 2;
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(col), 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    const mat = new MeshStandardMaterial({ vertexColors: true, roughness: 1, side: DoubleSide });
    this.landMats.push({ mat, base: new Color(1, 1, 1), snow: new Color(1.9, 2.0, 2.2) });
    const mesh = new Mesh(g, mat);
    mesh.name = 'far-ridges';
    return mesh;
  }

  private makeMotes(): Points {
    const n = 700;
    const r = rng(8);
    const a = new Float32Array(n * 3);
    for (let i = 0; i < n * 3; i++) a[i] = r();
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(a, 3));
    const mat = new ShaderMaterial({
      uniforms: this.moteUniforms,
      transparent: true, depthWrite: false, blending: AdditiveBlending,
      vertexShader: /* glsl */ `
        uniform float uTime; uniform vec3 uCenter; uniform float uAmount;
        varying float vA;
        ${GLSL_NOISE}
        void main() {
          vec3 box = vec3(40.0, 7.0, 40.0);
          vec3 local = position * box;
          local += vec3(uTime * 0.35, sin(uTime * 0.3 + position.x * 20.0) * 0.4, uTime * 0.12);
          vec3 wp = local + box * floor((uCenter - local) / box + 0.5);
          wp.y = local.y - box.y * floor(local.y / box.y) + 0.2;
          vec4 mv = viewMatrix * vec4(wp, 1.0);
          gl_Position = projectionMatrix * mv;
          float tw = 0.5 + 0.5 * sin(uTime * 2.0 + position.z * 40.0);
          vA = tw * (1.0 - smoothstep(12.0, 20.0, length(wp.xz - uCenter.xz))) * uAmount;
          gl_PointSize = clamp(55.0 / -mv.z, 1.0, 7.0);
        }`,
      fragmentShader: /* glsl */ `
        varying float vA;
        void main() {
          float d = length(gl_PointCoord - 0.5);
          float a = smoothstep(0.5, 0.0, d) * vA * 0.55;
          gl_FragColor = vec4(vec3(1.0, 0.8, 0.5) * a, a);
        }`,
    });
    const pts = new Points(g, mat);
    pts.frustumCulled = false;
    pts.name = 'motes';
    return pts;
  }

  /**
   * Dresses the scenery for the season and the light: snow on the islands, shore and far hills,
   * ice on the lake, pollen in summer sun, and the far scenery dark under the floodlights at night.
   */
  setSeason(season: SeasonState, sunlit: number, flood: number): void {
    const dark = 1 - 0.88 * flood;
    for (const l of this.landMats) l.mat.color.copy(l.base).lerp(l.snow, season.snow * 0.85).multiplyScalar(dark);
    const ice = season.lakeIce;
    this.water.color.set('#1b2c44').lerp(new Color('#9fb1c4').lerp(new Color('#c9d3de'), season.snow), ice);
    this.water.roughness = 0.16 + ice * 0.5;
    this.water.normalScale.setScalar(0.22 * (1 - ice * 0.9));
    this.water.envMapIntensity = 1.1 - ice * 0.4;
    this.moteUniforms.uAmount.value = season.pollen * sunlit;
    this.motes.visible = this.moteUniforms.uAmount.value > 0.01;
  }

  /** Pennant colours, cycled along the poles; null restores the stadium's own. */
  setFlagColours(colours: string[] | null): void {
    const list = (colours ?? FLAG_COLOURS).map((c) => new Color(c));
    for (let i = 0; i < this.flags.count; i++) this.flags.setColorAt(i, list[i % list.length]!);
    this.flags.instanceColor!.needsUpdate = true;
  }

  update(timeS: number, camera: Camera): void {
    this.wind.uTime.value = timeS;
    this.moteUniforms.uTime.value = timeS;
    this.moteUniforms.uCenter.value.copy(camera.position);
    if (this.water.normalMap) this.water.normalMap.offset.set(timeS * 0.004, timeS * 0.0025);
  }
}
